import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { GroundCommonContent } from "../../../../modules/content/components/GroundCommonContent/GroundCommonContent";
import { GroundMedia } from "../../../../modules/content/components/GroundMedia/GroundMedia";
import { GroundRichText } from "../../../../modules/content/components/GroundRichText/GroundRichText";
import { GroundSection } from "../../../../modules/content/components/GroundSection/GroundSection";
import type { ITeamMemberGridBlockProps } from "./TeamMemberGridBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;

export const TeamMemberGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: ITeamMemberGridBlockProps) => {
  if (!items?.length) return null;
  const Link = getComponent("Link");

  return (
    <GroundSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <GroundCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid gap-x-6 gap-y-12", columns[columnCount || 3])}>
          {items.map((member, index) => (
            <article className="group flex h-full flex-col" key={index}>
              <GroundMedia
                aspectRatio="3:4"
                className="bg-basalt"
                imageClassName="transition-transform duration-300 ease-out group-hover:scale-[1.015]"
                media={member.portrait.image}
                sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
              />
              <div className="flex flex-1 flex-col border-t border-weathered-steel pt-5">
                <h3 className="font-ground-display text-2xl font-bold leading-tight">
                  {member.name}
                </h3>
                {member.role ? (
                  <p className="mt-2 font-ground-mono text-xs text-primary">{member.role}</p>
                ) : null}
                {member.biography ? (
                  <GroundRichText
                    className="my-4 text-base text-basalt md:text-lg"
                    content={member.biography}
                  />
                ) : null}
                {member.links?.length ? (
                  <div className="mt-auto flex flex-wrap gap-4 border-t border-weathered-steel pt-3">
                    {member.links.map((link, linkIndex) => (
                      <Link
                        aria-label={link.accessibilityLabel || undefined}
                        className="inline-flex min-h-11 items-center font-ground-mono text-xs text-primary underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                        href={link.href}
                        key={linkIndex}
                        rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                        target={link.openInNewTab ? "_blank" : undefined}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </GroundSection>
  );
};

TeamMemberGridBlock.displayName = "Block::TeamMemberGridBlock";
